import React, { ChangeEvent } from "react";
import { Card } from "components/Card";
import { InputFormField } from "components/InputFormField";
import { Separator } from "components/Separator";
import { Footer } from "components/Footer";
import { Loading } from "components/Loading";
import { PlaceHolderNotExists } from "components/PlaceholderNotExists";
import { CheckBox } from "components/CheckBox";
import { RichTextEditor } from "components/RichTextEditor";
import { Button } from "components/Button";

import { Container, Wrapper, Subtitle, SideContainer } from "./styles";
import { EventProps } from "../EventNew/useEventNew";
import { InputImage } from "../../../components/InputImage";
import { InputPdf } from "../../../components/InputPdf";
import {
  ImageDetails,
  ImagePreview,
} from "../../Sellers/SellerDetails/Identity/styles";
import { useEventForm } from "./useEventForm";
import { SelectModified } from "components/SelectModified";
import { useHistory } from "react-router-dom";
import { formatDate } from "utils/formatDate";

interface EventFormProps {
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  handleSubmit: () => void;
  inputsValues: EventProps;
  touched: any;
  errors: any;
  changeFooter: string[];
  submitButton: boolean;
  hasError: boolean;
  isLoading: boolean;
  isEditing?: boolean;
  customHandleImageChange?: (file: File) => void;
  customHandlePdfChange?: (file: File) => void;
}

export const EventForm: React.FC<EventFormProps> = ({
  handleChange,
  handleSelectChange,
  inputsValues,
  touched,
  errors,
  changeFooter,
  submitButton,
  handleSubmit,
  hasError,
  isLoading,
  isEditing = false,
  customHandleImageChange,
  customHandlePdfChange,
}) => {
  const {
    handleImageChange,
    imagePath,
    logoIsPNG,
    handleCheckBoxChange,
    handlePdfChange,
  } = useEventForm(
    handleChange,
    customHandleImageChange,
    customHandlePdfChange
  );

  const { push } = useHistory();
  if (isLoading) {
    return <Loading />;
  }

  if (hasError) {
    return (
      <PlaceHolderNotExists title="Erro ao tentar conectar com o servidor" />
    );
  }

  return (
    <>
      <Container>
        <SideContainer>
          <form onSubmit={handleSubmit}>
            <Card title="Informações do Evento" typography="subtitle-medium">
              <Wrapper>
                <ImagePreview
                  imageUrl={imagePath ?? inputsValues.logo?.signedFileUrl ?? ""}
                  width={156}
                  height={156}
                />
                <ImageDetails>
                  Sempre que possível utilize um arquivo de proporção 1:1
                  <InputImage
                    error={touched.logo && !!errors.logo}
                    errorMessage="Nenhuma imagem encontrada"
                    errorByTypeImage={!logoIsPNG && !!imagePath}
                    errorMessageByTypeImage="Por favor, utilize arquivos em .PNG"
                    onChange={handleImageChange}
                  />
                </ImageDetails>
              </Wrapper>
              <Wrapper>
                <InputFormField
                  name="name"
                  label="Nome do Evento"
                  error={touched.name && !!errors.name}
                  mensageError="Inserir nome do evento"
                  value={inputsValues.name}
                  onChange={handleChange}
                  block
                  required
                />

                <InputFormField
                  name="organizer"
                  label="Organizador"
                  error={touched.organizer && !!errors.organizer}
                  mensageError="Inserir organizador"
                  value={inputsValues.organizer}
                  onChange={handleChange}
                  block
                />
              </Wrapper>

              <Wrapper>
                <RichTextEditor
                  value={inputsValues.description}
                  onChange={(value: string) => {
                    const event = {
                      target: {
                        name: "description",
                        value: value,
                      },
                    } as ChangeEvent<HTMLInputElement>;
                    handleChange(event);
                  }}
                  label="Descrição"
                  error={touched.description && !!errors.description}
                  mensageError="Inserir descrição"
                  block
                  placeholder="Digite a descrição do evento..."
                />
              </Wrapper>

              <Separator maxWidth="100%" marginY="0" />
              <Subtitle>Localização e Data</Subtitle>

              <Wrapper>
                <InputFormField
                  name="address"
                  label="Endereço"
                  error={touched.address && !!errors.address}
                  mensageError="Inserir endereço do evento"
                  value={inputsValues.address}
                  onChange={handleChange}
                  block
                  required
                />
              </Wrapper>

              <Wrapper>
                <InputFormField
                  name="dateDescription"
                  label="Descrição da Data"
                  placeholder="Descrição da data"
                  error={touched.dateDescription && !!errors.dateDescription}
                  mensageError={
                    errors.dateDescription || "Inserir descrição da data"
                  }
                  value={inputsValues.dateDescription}
                  onChange={handleChange}
                  block
                  required
                />
                <InputFormField
                  name="endDate"
                  label="Data e hora de Término"
                  type="datetime-local"
                  placeholder="Data e hora de término do evento"
                  error={touched.endDate && !!errors.endDate}
                  mensageError={errors.endDate || "Data inválida"}
                  value={
                    inputsValues.endDate
                      ? formatDate(new Date(inputsValues.endDate))
                      : undefined
                  }
                  onChange={handleChange}
                  block
                />
              </Wrapper>

              <Wrapper>
                <div style={{ width: "100%", height: "50%" }}>
                  <SelectModified
                    name="type"
                    value={inputsValues.type}
                    label="Tipo de Evento"
                    options={[
                      { label: "Público", value: "PUBLIC" },
                      { label: "Privado", value: "PRIVATE" },
                    ]}
                    onChange={handleSelectChange}
                    block
                  />
                </div>

                <InputFormField
                  name="dateComplement"
                  label="Complemento da Data"
                  error={touched.dateComplement && !!errors.dateComplement}
                  mensageError="Inserir complemento da data"
                  value={inputsValues.dateComplement}
                  onChange={handleChange}
                  block
                  required
                />
              </Wrapper>

              <Separator maxWidth="100%" marginY="0" />
              <Subtitle>Configurações</Subtitle>

              <Wrapper>
                <InputFormField
                  name="maximumCapacity"
                  label="Capacidade Máxima"
                  type="number"
                  error={touched.maximumCapacity && !!errors.maximumCapacity}
                  mensageError="Inserir capacidade máxima"
                  value={inputsValues.maximumCapacity.toString()}
                  onChange={handleChange}
                  block
                  required
                />

                <InputFormField
                  name="logoUrl"
                  label="URL do Logo"
                  error={touched.logoUrl && !!errors.logoUrl}
                  mensageError="Inserir URL do logo"
                  value={inputsValues.logo?.signedFileUrl}
                  onChange={handleChange}
                  block
                  disabled
                />
              </Wrapper>

              <Wrapper>
                <InputFormField
                  name="ticketPurchaseUrl"
                  label="Link de Compra de Ingresso"
                  placeholder="https://exemplo.com/ingressos"
                  error={
                    touched.ticketPurchaseUrl && !!errors.ticketPurchaseUrl
                  }
                  mensageError="URL inválida"
                  value={inputsValues.ticketPurchaseUrl || ""}
                  onChange={handleChange}
                  block
                />
              </Wrapper>

              <Wrapper>
                <CheckBox
                  label="Publicado"
                  name="isPublished"
                  value=""
                  checked={inputsValues.isPublished}
                  onChange={handleCheckBoxChange("isPublished")}
                />
              </Wrapper>

              <Wrapper>
                <CheckBox
                  label="Mentorias"
                  name="mentorshipsEnabled"
                  value=""
                  checked={inputsValues.mentorshipsEnabled}
                  onChange={handleCheckBoxChange("mentorshipsEnabled")}
                />
              </Wrapper>

              <Wrapper>
                <CheckBox
                  label="Exibir QR CODE do ingresso"
                  name="showTicketQrCode"
                  value=""
                  checked={inputsValues.showTicketQrCode}
                  onChange={handleCheckBoxChange("showTicketQrCode")}
                />
              </Wrapper>

              <Separator maxWidth="100%" marginY="0" />
              <Subtitle>Redes Sociais</Subtitle>

              <Wrapper>
                <InputFormField
                  name="socialMedia.websiteUrl"
                  label="Website"
                  placeholder="https://exemplo.com"
                  error={touched.websiteUrl && !!errors.websiteUrl}
                  mensageError="URL inválida"
                  value={inputsValues.socialMedia?.websiteUrl || ""}
                  onChange={handleChange}
                  block
                />

                <InputFormField
                  name="socialMedia.linkedinUrl"
                  label="LinkedIn"
                  placeholder="https://linkedin.com/company/exemplo"
                  error={touched.linkedinUrl && !!errors.linkedinUrl}
                  mensageError="URL inválida"
                  value={inputsValues.socialMedia?.linkedinUrl || ""}
                  onChange={handleChange}
                  block
                />
              </Wrapper>

              <Wrapper>
                <InputFormField
                  name="socialMedia.twitterUrl"
                  label="Twitter/X"
                  placeholder="https://twitter.com/exemplo"
                  error={touched.twitterUrl && !!errors.twitterUrl}
                  mensageError="URL inválida"
                  value={inputsValues.socialMedia?.twitterUrl || ""}
                  onChange={handleChange}
                  block
                />

                <InputFormField
                  name="socialMedia.instagramUrl"
                  label="Instagram"
                  placeholder="https://instagram.com/exemplo"
                  error={touched.instagramUrl && !!errors.instagramUrl}
                  mensageError="URL inválida"
                  value={inputsValues.socialMedia?.instagramUrl || ""}
                  onChange={handleChange}
                  block
                />
              </Wrapper>

              <Wrapper>
                <InputFormField
                  name="socialMedia.facebookUrl"
                  label="Facebook"
                  placeholder="https://facebook.com/exemplo"
                  error={touched.facebookUrl && !!errors.facebookUrl}
                  mensageError="URL inválida"
                  value={inputsValues.socialMedia?.facebookUrl || ""}
                  onChange={handleChange}
                  block
                />

                <InputFormField
                  name="socialMedia.youtubeUrl"
                  label="YouTube"
                  placeholder="https://youtube.com/c/exemplo"
                  error={touched.youtubeUrl && !!errors.youtubeUrl}
                  mensageError="URL inválida"
                  value={inputsValues.socialMedia?.youtubeUrl || ""}
                  onChange={handleChange}
                  block
                />
              </Wrapper>

              <Wrapper>
                <InputFormField
                  name="socialMedia.tiktokUrl"
                  label="TikTok"
                  placeholder="https://tiktok.com/@exemplo"
                  error={touched.tiktokUrl && !!errors.tiktokUrl}
                  mensageError="URL inválida"
                  value={inputsValues.socialMedia?.tiktokUrl || ""}
                  onChange={handleChange}
                  block
                />
              </Wrapper>

              <Separator maxWidth="100%" marginY="0" />
              <Subtitle>Mapa do Evento</Subtitle>

              <Wrapper>
                <InputPdf
                  error={
                    touched.mapPdf?.signedFileUrl &&
                    !!errors.mapPdf?.signedFileUrl
                  }
                  errorMessage="Erro ao fazer upload do PDF"
                  onChange={handlePdfChange}
                />
                <InputFormField
                  name="signedFileUrl"
                  label="PDF do Mapa"
                  placeholder="https://exemplo.com/mapa.pdf"
                  error={
                    touched.mapPdf?.signedFileUrl &&
                    !!errors.mapPdf?.signedFileUrl
                  }
                  mensageError="URL inválida"
                  value={inputsValues.mapPdf?.signedFileUrl || ""}
                  onChange={handlePdfChange}
                  disabled
                  block
                />
              </Wrapper>
            </Card>
          </form>
        </SideContainer>
      </Container>

      <Footer>
        <Button
          type="button"
          label="Descartar"
          size="medium"
          typeBtn="outline"
          onClick={() => push(`/events`)}
        />
        <Button
          type="button"
          label={isEditing ? "Salvar Alterações" : "Criar Evento"}
          size="medium"
          typeBtn="blue"
          disable={submitButton}
          onClick={handleSubmit}
        />
      </Footer>
    </>
  );
};
